/** @format — Documents list / table view */

import { ArrowDownUp, ArrowUpRight, ExternalLink } from "lucide-react";
import DocumentThumbnail from "./DocumentThumbnail";
import DocumentListActionButton from "./DocumentListActionButton";
import type { DocumentItem } from "../types";

const DESKTOP_COLS =
  "md:grid md:grid-cols-[56px_minmax(0,1fr)_minmax(72px,100px)_minmax(96px,110px)_minmax(72px,90px)_90px] md:items-center";

export default function DocumentsList({
  documents,
  onOpen,
  onGoToShipment,
  onToggleDateSort,
  showGoToShipment = true,
}: {
  documents: DocumentItem[];
  onOpen: (doc: DocumentItem) => void;
  onGoToShipment: (doc: DocumentItem, event?: React.MouseEvent) => void;
  onToggleDateSort?: () => void;
  showGoToShipment?: boolean;
}) {
  return (
    <div className="overflow-hidden rounded-xl border border-[#e9eaeb] bg-white">
      <div className={`hidden border-b border-neutral-200 ${DESKTOP_COLS}`}>
        <div className="px-2 py-2">
          <span className="text-sm font-medium text-[#0a0a0a]">Document</span>
        </div>
        <div className="px-2 py-2" />
        <div className="px-2 py-2 text-sm font-medium text-[#0a0a0a]">Type</div>
        <button
          type="button"
          onClick={onToggleDateSort}
          className="flex items-center gap-2 px-2 py-2 text-left text-sm font-medium text-[#0a0a0a] hover:bg-neutral-50"
        >
          Date
          <ArrowDownUp className="h-4 w-4 text-neutral-500 opacity-50" strokeWidth={2} />
        </button>
        <div className="px-2 py-2 text-sm font-medium text-[#0a0a0a]">Size</div>
        <div className="px-2 py-2" />
      </div>

      <ul className="divide-y divide-neutral-200">
        {documents.map((document) => (
          <li key={document.id}>
            {/* Mobile */}
            <div className="flex items-center gap-2 px-3 py-3 transition-colors hover:bg-[#F5F7FA] md:hidden">
              <DocumentThumbnail document={document} size="list" />
              <button
                type="button"
                className="min-w-0 flex-1 text-left"
                onClick={() => onOpen(document)}
              >
                <p className="truncate text-sm font-medium text-[#0a0a0a]">{document.name}</p>
                <p className="mt-0.5 truncate text-xs text-[#475367]">{document.dateLabel}</p>
              </button>
              <div className="flex shrink-0 items-center">
                <DocumentListActionButton label="Open" onClick={() => onOpen(document)}>
                  <ArrowUpRight className="h-4 w-4" strokeWidth={2} />
                </DocumentListActionButton>
                {showGoToShipment ? (
                  <DocumentListActionButton
                    label="Go to shipment"
                    tooltipPlacement="bottom"
                    onClick={(event) => {
                      event.stopPropagation();
                      onGoToShipment(document, event);
                    }}
                  >
                    <ExternalLink className="h-4 w-4" strokeWidth={2} />
                  </DocumentListActionButton>
                ) : null}
              </div>
            </div>

            {/* Desktop */}
            <div
              className={`hidden items-center transition-colors hover:bg-[#F5F7FA] md:grid ${DESKTOP_COLS}`}
            >
              <div className="flex h-[60px] items-center px-2 py-[7.5px]">
                <DocumentThumbnail document={document} size="list" />
              </div>

              <button
                type="button"
                className="min-w-0 truncate px-2 py-[7.5px] text-left text-sm text-[#0a0a0a] md:h-[60px]"
                onClick={() => onOpen(document)}
                title={document.name}
              >
                {document.name}
              </button>

              <p className="hidden px-2 py-[7.5px] text-sm text-[#0a0a0a] md:block">{document.type}</p>
              <p className="hidden px-2 py-[7.5px] text-sm text-[#0a0a0a] md:block">
                {document.dateShort}
              </p>
              <p className="hidden px-2 py-[7.5px] text-sm text-[#0a0a0a] md:block">
                {document.sizeLabel}
              </p>

              <div className="flex h-[60px] items-center justify-end px-2 md:justify-center">
                <DocumentListActionButton label="Open" onClick={() => onOpen(document)}>
                  <ArrowUpRight className="h-4 w-4" strokeWidth={2} />
                </DocumentListActionButton>
                {showGoToShipment ? (
                  <DocumentListActionButton
                    label="Go to shipment"
                    tooltipPlacement="bottom"
                    onClick={(event) => {
                      event.stopPropagation();
                      onGoToShipment(document, event);
                    }}
                  >
                    <ExternalLink className="h-4 w-4" strokeWidth={2} />
                  </DocumentListActionButton>
                ) : null}
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
